/*
 * Post images come from Ghost at full size: the hero on a typical article is a
 * 1.5 MB PNG rendered into a 1200px slot. These helpers point them at the image
 * CDN instead, which resizes, re-encodes to WebP/AVIF and serves from S3
 * through CloudFront. The same 1.5 MB hero lands at about 124 KB.
 *
 * Anything that is not a Ghost content image is returned untouched, so avatars,
 * data URIs and third-party images keep working.
 */

/*
 * The CDN is a pure S3 read through CloudFront: no function runs in the
 * serving path. That matters because this account's Lambda concurrency limit
 * is 10, and an earlier design that resized on demand starved the platform.
 *
 * Because nothing can generate a derivative on the fly, an image is only
 * pointed at the CDN when it is in the manifest, which is written by
 * image-service/scripts/warm-local.mjs. Anything else keeps its origin URL,
 * so a newly published post shows its image immediately and simply does not
 * get the WebP benefit until the next warm.
 */
import MANIFEST from './imageManifest.json';

/*
 * Three sources of truth about what is warmed, cheapest first:
 *  1. the manifest baked into this bundle at build time,
 *  2. whatever the CDN's manifest.json says, fetched once per session,
 *  3. paths this browser warmed itself and has not seen in a manifest yet.
 *
 * A path in none of them is served from the origin and queued for warming, so
 * the picture is never late and the next visitor gets the WebP copy.
 */
const WARMED = new Set(MANIFEST.paths || []);
const LOCAL_KEY = 'lx_img_warmed';

try {
  const seen = JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]');
  if (Array.isArray(seen)) seen.forEach((p) => WARMED.add(p));
} catch { /* private mode, or corrupt value */ }

function rememberLocally(paths) {
  paths.forEach((p) => WARMED.add(p));
  try {
    // Bounded: the baked manifest covers the bulk, this only carries the tail.
    const keep = [...WARMED].slice(-1500);
    localStorage.setItem(LOCAL_KEY, JSON.stringify(keep));
  } catch { /* nothing we can do, and nothing that matters */ }
}

// Refresh from the CDN once per session. Failure is silent: the baked manifest
// is already a good answer.
let refreshed = false;
function refreshManifest() {
  if (refreshed || typeof fetch === 'undefined') return;
  refreshed = true;
  fetch(`${CDN}/manifest.json`, { cache: 'default' })
    .then((r) => (r.ok ? r.json() : null))
    .then((m) => { if (m && Array.isArray(m.paths)) m.paths.forEach((p) => WARMED.add(p)); })
    .catch(() => {});
}

/*
 * Misses are collected and sent in one batch rather than a request per image,
 * and only after the page has settled, so warming never competes with the
 * render it is meant to improve.
 */
const pending = new Set();
const asked = new Set();
let timer = null;

function queueWarm(path) {
  if (asked.has(path) || WARMED.has(path)) return;
  pending.add(path);
  clearTimeout(timer);
  timer = setTimeout(flushWarm, 2000);
}

function flushWarm() {
  const paths = [...pending].slice(0, 20);
  if (!paths.length) return;
  paths.forEach((p) => { pending.delete(p); asked.add(p); });
  fetch(`${CDN_API}/warm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ paths }),
    keepalive: true,
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => { if (d && Array.isArray(d.warmed) && d.warmed.length) rememberLocally(d.warmed); })
    .catch(() => {})
    .finally(() => { if (pending.size) { clearTimeout(timer); timer = setTimeout(flushWarm, 2000); } });
}
const CDN_ENABLED = String(process.env.REACT_APP_IMAGE_CDN_ENABLED || '') !== 'false';

const CDN =
  (typeof window !== 'undefined' && window.electronEnv?.imageCdnUrl) ||
  process.env.REACT_APP_IMAGE_CDN_URL ||
  'https://img.app.lxop.in';

// The warm endpoint is on the API, not the CDN: CloudFront fronts a bucket and
// would not know what to do with a POST.
// Warming goes through the same host as the images: CloudFront routes /warm to
// the API, so nothing here depends on an execute-api id that changes whenever
// the API is recreated.
const CDN_API = CDN;

// https://www.learnxops.com/content/images/2026/09/day-34.png  ->  2026/09/day-34.png
// Ghost's own resized variants (/content/images/size/w600/…) collapse to the
// same source path, so we never resize an already-resized image.
const GHOST_IMAGE = /^https?:\/\/(?:www\.)?learnxops\.com\/content\/images\/(?:size\/[^/]+\/)?(\d{4}\/\d{2}\/[^?#]+)/i;
const SUPPORTED   = /\.(png|jpe?g|webp|gif|avif)$/i;

// Must match --presets in image-service/scripts/warm-local.mjs.
export const PRESETS = MANIFEST.presets || [400, 800, 1200, 1600];

/** The Ghost content path for a URL, or null if the CDN cannot serve it. */
export function sourcePath(url) {
  if (!CDN_ENABLED) return null;          // kill switch: serve origin URLs untouched
  if (typeof url !== 'string') return null;
  const m = url.match(GHOST_IMAGE);
  if (!m) return null;
  const path = m[1];
  if (!SUPPORTED.test(path)) return null;
  if (WARMED.has(path)) return path;
  // Not warmed means no object in S3, which CloudFront answers with a 403.
  // Serve the origin copy now and queue the derivative for next time.
  if (CDN_ENABLED) { refreshManifest(); queueWarm(path); }
  return null;
}

/**
 * An author avatar at the size it is actually drawn.
 *
 * Ghost hands back whatever Gravatar URL the author's account carries, which
 * here is ?s=250 — 79KB for a picture rendered at 24 CSS pixels on /posts.
 * Gravatar will render any size, so ask for the one needed.
 *
 * Only Gravatar is rewritten. A self-hosted avatar is left alone rather than
 * pushed through the preset pipeline, whose smallest size is 400px and so
 * would make matters worse.
 */
export function avatarUrl(url, cssSize = 24) {
  if (typeof url !== 'string' || !url) return url;
  try {
    const u = new URL(url, window.location.origin);
    if (!/(^|\.)gravatar\.com$/i.test(u.hostname)) return url;
    // Twice the CSS size covers a 2x display; beyond that the difference is
    // invisible at avatar dimensions.
    u.searchParams.set('s', String(Math.min(512, Math.max(16, Math.round(cssSize * 2)))));
    return u.toString();
  } catch {
    return url;
  }
}

/** Nearest preset at or above the width actually needed. */
function presetFor(width) {
  const w = Number(width) || 1200;
  return PRESETS.find((p) => p >= w) || PRESETS[PRESETS.length - 1];
}

/**
 * CDN URL for an image at a given rendered width.
 * Returns the original URL unchanged when it is not a Ghost content image.
 */
export function imageUrl(url, width = 1200) {
  const path = sourcePath(url);
  if (!path) return url;
  return `${CDN}/w${presetFor(width)}/${path}`;
}

/** A srcSet across the presets a slot could plausibly need. */
export function imageSrcSet(url, widths = [400, 800, 1200, 1600]) {
  const path = sourcePath(url);
  if (!path) return undefined;
  return widths
    .map((w) => presetFor(w))
    .filter((w, i, a) => a.indexOf(w) === i)
    .map((w) => `${CDN}/w${w}/${path} ${w}w`)
    .join(', ');
}

/**
 * Everything an <img> needs for a responsive, CDN-served image.
 * Spread it onto the element: <img {...imageProps(src, 1200, '100vw')} alt="" />
 */
export function imageProps(url, width = 1200, sizes) {
  const path = sourcePath(url);
  if (!path) return { src: url };
  return {
    src: imageUrl(url, width),
    srcSet: imageSrcSet(url),
    sizes: sizes || `(max-width: 768px) 100vw, ${width}px`,
  };
}

/**
 * Prepare Ghost post HTML for rendering.
 *
 * Two passes, both needed because Ghost emits markup with no classes:
 *  - <img> tags are pointed at the CDN. Ghost's own srcset of full-size
 *    variants is dropped, otherwise the browser still pulls megabytes.
 *  - <table> is wrapped in a scroll container, so a wide table scrolls
 *    instead of overflowing the article column on a phone.
 *
 * Parsed with DOMParser rather than regex so malformed markup cannot produce
 * a broken document, and any failure returns the original HTML untouched.
 */
export function rewriteHtmlImages(html, width = 1200) {
  if (!html || typeof window === 'undefined' || typeof window.DOMParser === 'undefined') return html;
  try {
    const doc = new window.DOMParser().parseFromString(html, 'text/html');
    let touched = 0;
    doc.querySelectorAll('img').forEach((img) => {
      const path = sourcePath(img.getAttribute('src') || '');
      if (!path) return;
      const original = img.getAttribute('src');
      img.setAttribute('src', imageUrl(original, width));
      const set = imageSrcSet(original);
      if (set) {
        img.setAttribute('srcset', set);
        if (!img.getAttribute('sizes')) img.setAttribute('sizes', `(max-width: 768px) 100vw, ${width}px`);
      } else {
        img.removeAttribute('srcset');
      }
      if (!img.getAttribute('loading')) img.setAttribute('loading', 'lazy');
      if (!img.getAttribute('decoding')) img.setAttribute('decoding', 'async');
      touched += 1;
    });

    doc.querySelectorAll('table').forEach((table) => {
      if (table.parentElement?.classList?.contains('lx-table-wrap')) return;
      const wrap = doc.createElement('div');
      wrap.className = 'lx-table-wrap';
      table.parentNode.insertBefore(wrap, table);
      wrap.appendChild(table);
      touched += 1;
    });

    return touched ? doc.body.innerHTML : html;
  } catch {
    return html;   // a rewrite failure must never blank the article
  }
}
